"use client";

import { useEffect, useMemo } from "react";

import { useConnections } from "@/lib/hooks/use-api";
import { usePreloadMessages } from "@/lib/hooks/messages/usePreloadMessages";
import { useSimpleMessageContext } from "@/lib/contexts/SimpleMessageContext";

interface MessagePreloaderProps {
  enabled?: boolean;
}

export function MessagePreloader({ enabled = true }: MessagePreloaderProps) {
  const { data: connections } = useConnections();

  // Test that SimpleMessageProvider is available
  const messageContext = useSimpleMessageContext();

  // Map connections to the correct parameters for message preloading
  const connection1 = connections[0];
  const connection2 = connections[1];
  const connection3 = connections[2];
  const connection4 = connections[3];
  const connection5 = connections[4];

  // Always call the same number of hooks, but pass empty values when no connection exists
  const connection1Preload = usePreloadMessages({
    inboxId: connection1?.connectionId || "",
    provider: connection1?.providerConfigKey || "",
    enabled: enabled && connections.length > 0,
  });

  const connection2Preload = usePreloadMessages({
    inboxId: connection2?.connectionId || "",
    provider: connection2?.providerConfigKey || "",
    enabled: enabled && connections.length > 1,
  });

  const connection3Preload = usePreloadMessages({
    inboxId: connection3?.connectionId || "",
    provider: connection3?.providerConfigKey || "",
    enabled: enabled && connections.length > 2,
  });

  const connection4Preload = usePreloadMessages({
    inboxId: connection4?.connectionId || "",
    provider: connection4?.providerConfigKey || "",
    enabled: enabled && connections.length > 3,
  });

  const connection5Preload = usePreloadMessages({
    inboxId: connection5?.connectionId || "",
    provider: connection5?.providerConfigKey || "",
    enabled: enabled && connections.length > 4,
  });

  // Log preload status in development
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      const preloadHooks = [
        connection1Preload,
        connection2Preload,
        connection3Preload,
        connection4Preload,
        connection5Preload,
      ];
      const loadingCount = preloadHooks.filter((hook) => hook.isLoading).length;
      const errorCount = preloadHooks.filter((hook) => hook.error).length;
      const dataCount = preloadHooks.filter((hook) => hook.hasData).length;

      if (loadingCount > 0 || errorCount > 0 || dataCount > 0) {
        console.log(
          `MessagePreloader: ${loadingCount} loading, ${errorCount} errors, ${dataCount} with data`
        );
      }

      // Log SimpleMessageProvider availability
      console.log("SimpleMessageProvider available:", !!messageContext);

      // Log connection details
      if (connections.length > 0) {
        console.log(
          "Connections for preloading:",
          connections.map((conn: any) => ({
            connectionId: conn.connectionId,
            providerConfigKey: conn.providerConfigKey,
            provider: conn.provider,
          }))
        );
      }
    }
  }, [
    connection1Preload,
    connection2Preload,
    connection3Preload,
    connection4Preload,
    connection5Preload,
    messageContext,
    connections,
  ]);

  // This component doesn't render anything
  return null;
}
